import React, { useState, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";

import CustomSelect from "../../utils/CustomSelect";

const Chart3DWrapper = lazy(() => import("../../components/charts/Chart3DWrapper"));

const safe4DDate = (v) => {
    if (!v || v === "0000-00-00") return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const isExpertiseDoc = (doc) => {
    const titre = (doc?.Titre ?? "").toLowerCase();
    return doc?._Type_Document === "Expertise" || titre.startsWith("expertise");
};

const getExpertiseDate = (doc) => safe4DDate(doc?.Date_Convoc) || safe4DDate(doc?.Date_evenement);

const getSiteName = (uuid, sites) => {
    const s = sites?.find((x) => x.UUID_ === uuid);
    return s?.Nom || "Inconnu";
};

const ExpertiseTab = ({ lstDocs = [], sites = [], filters, setFilters, loadingL = false }) => {
    const [filteredData, setFilteredData] = useState([]);

    const baseExpertises = lstDocs.filter(isExpertiseDoc);

    const filterExpertises = (docs, f) => {
        let filtered = docs;

        // CA
        if (f.ca !== "Tous") {
            filtered = filtered.filter((d) => d.CA === f.ca);
        }

        // Clôture
        if (f.cloture !== "Tous") {
            const wantClosed = f.cloture === "Clôturées";
            filtered = filtered.filter((d) => Boolean(d.Cloture) === wantClosed);
        }

        // Dates
        if (f.minDate) {
            filtered = filtered.filter((d) => {
                const dd = getExpertiseDate(d);
                return dd ? dd >= f.minDate : false;
            });
        }
        if (f.maxDate) {
            filtered = filtered.filter((d) => {
                const dd = getExpertiseDate(d);
                return dd ? dd <= f.maxDate : false;
            });
        }

        return filtered;
    };

    useEffect(() => {
        setFilteredData(filterExpertises(baseExpertises, filters));
    }, [lstDocs, filters]);

    // Options filtres
    const optCA = ["Tous", ...new Set(baseExpertises.map((d) => d.CA).filter(Boolean))];

    // KPI années (counts)
    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    const countByYear = (year) =>
        filteredData.reduce((acc, doc) => {
            const dd = getExpertiseDate(doc);
            if (!dd) return acc;
            return dd.getFullYear() === year ? acc + 1 : acc;
        }, 0);

    const totalCurrentYear = countByYear(currentYear);
    const totalPreviousYear = countByYear(previousYear);

    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

    const countBetween = (arr, start, endExclusive) =>
        arr.reduce((acc, doc) => {
            const dd = getExpertiseDate(doc);
            return dd && dd >= start && dd < endExclusive ? acc + 1 : acc;
        }, 0);

    // on garde CA + cloture, mais on ignore le filtre date pour ces KPI “généraux”
    const filteredNoDate = filterExpertises(baseExpertises, {
        ...filters,
        minDate: null,
        maxDate: null,
    });

    const now = new Date();
    const endThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1); // exclusif
    const startLast12 = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const startPrev12 = new Date(now.getFullYear(), now.getMonth() - 23, 1);
    const endPrev12 = startLast12;

    const totalLast12 = countBetween(filteredNoDate, startLast12, endThisMonth);
    const totalPrev12 = countBetween(filteredNoDate, startPrev12, endPrev12);


    const [top10BySite, setTop10BySite] = useState([]);

    useEffect(() => {
        const map = {};

        filteredData.forEach((doc) => {
            const uuid = doc.UUID_Site_ || "INCONNU";
            map[uuid] = (map[uuid] || 0) + 1;
        });

        const top = Object.entries(map)
            .map(([uuid, count]) => ({ x: getSiteName(uuid, sites), y: count }))
            .sort((a, b) => b.y - a.y)
            .slice(0, 10)
            .filter((i) => i.x && typeof i.y === "number");

        setTop10BySite(top);
    }, [filteredData, sites]);

    // Top 10 CA (nombre d'expertises)
    const [top10ByCA, setTop10ByCA] = useState([]);

    useEffect(() => {
        const map = {};

        filteredData.forEach((doc) => {
            const ca = doc.CA || "Inconnu";
            map[ca] = (map[ca] || 0) + 1;
        });

        const top = Object.entries(map)
            .map(([ca, count]) => ({ x: ca, y: count }))
            .sort((a, b) => b.y - a.y)
            .slice(0, 10);

        setTop10ByCA(top);
    }, [filteredData]);

    console.log(top10ByCA);

    return (
        <>
            {/* FILTRES */}
            <div className="bg-white mt-4 md:mt-8 p-4 rounded-lg">
                <div className="flex xl:flex-row flex-col gap-4 w-full">
                    <div className="flex md:flex-row flex-col gap-4 w-full xl:w-1/2">
                        <div className="w-full">
                            <CustomSelect
                                label={"Chargé d'affaires (CA)"}
                                options={optCA}
                                value={filters.ca}
                                onChange={(val) => setFilters((prev) => ({ ...prev, ca: val }))}
                            />
                        </div>

                    </div>

                    <div className="flex md:flex-row flex-col items-center gap-4 w-full xl:w-1/2">
                        <div className="flex flex-row justify-between h-full">
                            <div>
                                <span className="text-gray-600 text-sm">À partir du :</span>
                                <Suspense fallback={<div>Loading...</div>}>
                                    <DatePickerComponent
                                        placeholder="Date minimale"
                                        format={"dd/MM/yyyy"}
                                        value={filters.minDate}
                                        change={(e) => setFilters((prev) => ({ ...prev, minDate: e.value }))}
                                    />
                                </Suspense>
                            </div>
                            <div>
                                <span className="text-gray-600 text-sm">Jusqu'au :</span>
                                <Suspense fallback={<div>Loading...</div>}>
                                    <DatePickerComponent
                                        placeholder="Date maximale"
                                        format={"dd/MM/yyyy"}
                                        value={filters.maxDate}
                                        change={(e) => setFilters((prev) => ({ ...prev, maxDate: e.value }))}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="lg:w-1/2">
                    <div className="relative w-full">
                        <div className="bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full">
                            <div className="p-5 h-full">
                                <div className="flex justify-between">
                                    <div className="text-white text-center">
                                        <i className="fa-solid fa-file-circle-check fa-lg"></i>
                                    </div>
                                    <div
                                        className={`flex justify-items-center px-2 rounded-full h-6 font-semibold text-white bg-white text-sm ${loadingL ? "animate-pulse" : ""
                                            }`}
                                    />
                                </div>
                                <div className="flex-1 ml-2 w-full">
                                    <div className="mt-3 font-bold text-white text-3xl leading-8">
                                        {loadingL ? (
                                            <div className="bg-gray-100 rounded-md w-20 h-8 animate-pulse"></div>
                                        ) : (
                                            filteredData.length
                                        )}
                                    </div>
                                    <div className="mt-1 text-gray-200 text-base">Expertises</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <motion.div className="bg-white mt-4 md:mt-8 p-4 rounded-lg">
                        <Suspense fallback={<div>Loading...</div>}>
                            <Chart3DWrapper id="exp-top-ca" dataSource={top10ByCA} title="Top 10 de Chargés d'Affaires (nombre d'expertises)" />
                        </Suspense>
                    </motion.div>
                </div>

                <div className="lg:flex flex-col justify-between mt-4 md:mt-6 lg:mt-0 lg:ml-4 xl:ml-8 lg:w-1/2">
                    <div className="bg-white p-4 rounded-lg w-full">
                        <Suspense fallback={<div>Loading...</div>}>
                            <Chart3DWrapper id="exp-top-site" dataSource={top10BySite} title="Top 10 du nombre d'expertises par Site" />
                        </Suspense>
                    </div>

                    {/* <div className="sm:flex justify-between items-center gap-x-6 xl:gap-x-8 mt-4 md:mt-6">
                        <div className="relative mt-4 sm:mt-0 w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full h-44 text-white">
                                <p className="font-bold text-2xl text-center uppercase">12 derniers mois</p>
                                <p className="font-medium text-sm text-center uppercase tracking-widest">
                                    Nombre d'expertises
                                </p>
                                <h6 className="font-bold text-white text-3xl leading-8">
                                    {loadingL ? <div className="bg-gray-100 rounded-md w-20 h-8 animate-pulse" /> : totalLast12}
                                </h6>
                            </div>
                        </div>

                        <div className="relative mt-4 sm:mt-0 w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full h-44 text-white">
                                <p className="font-bold text-2xl text-center uppercase">12 mois précédents</p>
                                <p className="font-medium text-sm text-center uppercase tracking-widest">
                                    Nombre d'expertises
                                </p>
                                <h6 className="font-bold text-white text-3xl leading-8">
                                    {loadingL ? <div className="bg-gray-100 rounded-md w-20 h-8 animate-pulse" /> : totalPrev12}
                                </h6>
                            </div>
                        </div>
                    </div> */}

                </div>
            </div>
            {/* 
            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="bg-white p-4 rounded-lg w-full">

                </div>
            </div> */}
        </>
    );
};

export default ExpertiseTab;
